import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { subDays } from 'date-fns';

import { AuthService } from '../auth/auth.service';
import { KpiType } from '../coaching-insight/insight-widget/model/kpi-type.model';
import { actions, addPageAction } from '../coaching/actions';
import { StateToProps } from '../store';
import { useDispatcher, UseStore } from '../store.decorator';
import { PeriodType } from '../widget/date-range.widget.model';

import { OaScoreType } from './models/oa-score-type.enum';
import { RowType } from './models/row-type.enum';
import { clearState, closeAllCharts, reload } from './my-coaching-card.action';
import { TranslationKeyMap } from './my-coaching-card.i18n';
import { coachingCardReducer } from './my-coaching-card.reducer';
import { CoachingCardState } from './my-coaching-card.state';
import { CmpsTrendViewDialogComponent } from './trend-view-dialog/trend-view-dialog.component';

@Component({
    selector: '[cmpsMyCoachingCard]',
    templateUrl: './my-coaching-card.component.html',
})
@UseStore<CoachingCardState>('coachingCard', coachingCardReducer)
export class MyCoachingCardComponent
    implements OnInit, OnDestroy, StateToProps<CoachingCardState>
{
    state: CoachingCardState;

    /** @private */ _isExpanded: boolean;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _dialogRef: CmpsDialogRef<
        CmpsTrendViewDialogComponent,
        any
    >;

    constructor(
        private readonly _authService: AuthService,
        private readonly _dialog: CmpsDialog,
        private readonly _router: Router,
        private readonly _activatedRoute: ActivatedRoute,
    ) {
        this._isExpanded = false;
    }

    ngOnInit(): void {
        this._onReload();
    }

    ngOnDestroy(): void {
        useDispatcher(this, clearState({}));
    }

    /** @private */
    _trendView(rowType: RowType): void {
        const rowTypeName = RowType[rowType];

        addPageAction(actions.MyCoachingCard.TREND_VIEW_CLICK, {
            rowType: rowTypeName,
        });

        const kpiType = this._mapRowTypeToKpiType(rowType);
        const chartData = this.state.trendDataMap.get(kpiType);

        this._dialogRef = this._dialog.open(CmpsTrendViewDialogComponent, {
            canClose: () => true,
            data: chartData,
            movable: true,
        });
    }

    /** @private */
    _toggleExpand(rowType?: RowType): void {
        const rowTypeName = RowType[rowType];

        addPageAction(actions.MyCoachingCard.TREND_VIEW_CLICK, {
            rowType: rowTypeName,
        });
        addPageAction(actions.MyCoachingCard.TOGGLE_CLICK, { rowType });

        this._isExpanded = !this._isExpanded;

        const cardElement =
            document.getElementsByClassName('coaching-card')[0].parentElement
                .classList;
        cardElement.toggle('insight-component-item--expanded');

        if (this._isExpanded && rowType != null) {
            const kpiType = this._mapRowTypeToKpiType(rowType);
            this._navigate(kpiType);
        }
    }

    /**
     * Maps RowType to KpiType.
     * @param rowType The RowType to map.
     * @returns The corresponding KpiType.
     */
    private _mapRowTypeToKpiType(rowType: RowType): KpiType {
        switch (rowType) {
            case RowType.MI2:
                return KpiType.MANAGEMENT_IMPROVEMENT_INDEX;
            case RowType.FLMI2:
                return KpiType.FL_MANAGEMENT_IMPROVEMENT_INDEX;
            case RowType.WP:
                return KpiType.WINNING_PERCENTAGE;
            case RowType.FLWP:
                return KpiType.FL_WINNING_PERCENTAGE;
            case RowType.FLOA:
                return KpiType.FRONT_LINE_OA;
            default:
                return KpiType.OPPORTUNITY_ALIGNMENT;
        }
    }

    /** @private */
    _onSelectItem(item: any): void {
        addPageAction(actions.MyCoachingCard.SELECT_ITEM_CLICK, {
            id: item.id,
        });

        switch (item.id) {
            case OaScoreType.Launch_OA:
                this._toggleExpand();
                break;
        }
    }

    /** @private */
    _onReload(): void {
        addPageAction(actions.MyCoachingCard.RELOAD_CLICK);

        const today = new Date();
        useDispatcher(
            this,
            reload({
                periodTypeId: PeriodType.ROLLING_30,
                effectiveDate: today,
                startDate: subDays(today, 29),
                endDate: today,
                userId: this._authService.user.id,
            }),
        );
        useDispatcher(this, closeAllCharts({}));
    }

    /** @private */
    _onRowClickToggleExpand(event: RowType): void {
        const rowTypeName = RowType[event];

        addPageAction(actions.MyCoachingCard.TREND_VIEW_CLICK, {
            rowType: rowTypeName,
        });
        this._toggleExpand(event);
    }

    private _navigate(kpiTypeId: KpiType): void {
        void this._router.navigate([], {
            relativeTo: this._activatedRoute,
            queryParams: {
                kpiTypeId,
                periodTypeId: PeriodType.ROLLING_30,
            },
            queryParamsHandling: 'merge',
        });
    }
}
